/**
 * Zod request schemas for the fiscal Edge Function (plan 02 §3.1 Task 1).
 * Shared by the Edge handlers (server validation) and the typed client callers.
 */
import { z } from 'zod';

const money = z.number().int().nonnegative();

export const FiscalLineItemSchema = z.object({
  name: z.string().min(1),
  quantity: z.number().positive(),
  unit_price: money,
  tax_rate_code: z.enum(['A', 'B', 'C', 'EXEMPT']),
  total: money,
  product_ref: z.string().optional(),
});

export const IssueReceiptRequestSchema = z.object({
  tenant_id: z.string().uuid(),
  location_id: z.string().uuid(),
  device_id: z.string().uuid(),
  payment_id: z.string().uuid(),
  idempotency_key: z.string().min(1),
  operator_ref: z.string().min(1),
  items: z.array(FiscalLineItemSchema).min(1),
  payments: z
    .array(
      z.object({ method: z.enum(['cash', 'card', 'bank_transfer', 'voucher']), amount: money }),
    )
    .min(1),
  customer_tax_id: z.string().optional(),
  issued_at: z.string().min(1),
  meta: z.record(z.string()).optional(),
});

export const VoidReceiptRequestSchema = z.object({
  tenant_id: z.string().uuid(),
  device_id: z.string().uuid(),
  fiscal_receipt_id: z.string().uuid(),
  idempotency_key: z.string().min(1),
  reason: z.string().min(1),
  operator_ref: z.string().min(1),
  /** present for partial refunds -> credit_note (negative total_amount) */
  credit_note_amount: money.optional(),
});

export const ZReportRequestSchema = z.object({
  tenant_id: z.string().uuid(),
  location_id: z.string().uuid(),
  device_id: z.string().uuid(),
  business_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  idempotency_key: z.string().min(1),
  operator_ref: z.string().min(1),
});

export type IssueReceiptRequestInput = z.infer<typeof IssueReceiptRequestSchema>;
export type VoidReceiptRequestInput = z.infer<typeof VoidReceiptRequestSchema>;
export type ZReportRequestInput = z.infer<typeof ZReportRequestSchema>;
